import { Dialog, Icon, Spinner } from '@blueprintjs/core'
import { lang, toast } from 'botpress/shared'
import _ from 'lodash'
import ms from 'ms'
import React, { Component } from 'react'

import api from '~/app/api'
import { formDataStore } from '../../../../token-store'
import { sanitizeBotId } from '../CreateBot-screens/CreateBotModal'
import style from './ImportBotModal.scss'

interface Props {
  onCreateBotSuccess: () => void
  toggle: () => void
  isOpen: boolean
}

interface State {
  botId: string
  error: any
  filePath: string | null
  fileSize: string | null
  fileContent: Buffer | null
  isIdTaken: boolean
  isExistingBot: boolean
  isProcessing: boolean
  overwrite: boolean
  yourself?: boolean
  progress: number
  isDragging: boolean
}

const defaultState: State = {
  botId: '',
  error: null,
  filePath: null,
  fileSize: null,
  fileContent: null,
  isIdTaken: false,
  isExistingBot: false,
  isProcessing: false,
  overwrite: true,
  yourself: true,
  progress: 0,
  isDragging: false
}

class ImportBotModal extends Component<Props, State> {
  private _form: HTMLFormElement | null = null
  savedFormData = formDataStore.get() || {}
  state: State = { ...defaultState }

  formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return '0 Bytes'
    }
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  generateRandomString = (length: number = 16): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length)
      result += chars[randomIndex]
    }
    return result
  }

  importBot = async (e: React.FormEvent) => {
    e.preventDefault()
    if (this.isButtonDisabled) {
      return
    }

    this.setState({ isProcessing: true, progress: 0, error: null })
    const randomString = this.generateRandomString(20)
    const oldBotId = this.state.botId
    let newBotId: string
    let email: string

    if (this.state.yourself) {
      newBotId = `${randomString}-${this.state.botId.split('-')[1] || this.state.botId}`
      email = this.savedFormData.email
    } else {
      newBotId = oldBotId
      email = '-' // keeping both empty so that the bot is imported for the original owner's email id
    }

    try {
      await api
        .getSecured({ timeout: ms('20m') })
        .post(
          `/admin/workspace/bots/${this.savedFormData.fullName}/${oldBotId}/${newBotId}/${email}/import?overwrite=${this.state.overwrite}`,
          this.state.fileContent,
          {
            headers: { 'Content-Type': 'application/tar+gzip' },
            onUploadProgress: evt => {
              if (evt.total) {
                this.setState({ progress: Math.round((evt.loaded / evt.total) * 100) })
              }
            }
          }
        )

      toast.success('admin.workspace.bots.import.successful', this.state.botId)
      this.props.onCreateBotSuccess()
      setTimeout(() => {
        window.location.reload() // reloading for the bot creation limit check
      }, 500)
      this.toggleDialog()
    } catch (error: any) {
      this.setState({ error: error.message || 'Import failed', isProcessing: false })
    } finally {
      this.setState({ progress: 0 })
    }
  }

  handleBotIdChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ botId: sanitizeBotId(e.currentTarget.value), overwrite: false })

  handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!this.state.isDragging) {
      this.setState({ isDragging: true })
    }
  }

  handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (this.state.isDragging) {
      this.setState({ isDragging: false })
    }
  }

  handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ isDragging: false })
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      this.handleFileChanged(e.dataTransfer.files)
    }
  }

  handleFileChanged = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return
    }

    const file = files[0]
    const fr = new FileReader()
    fr.readAsArrayBuffer(file)
    fr.onload = loadedEvent => {
      this.setState({ fileContent: _.get(loadedEvent, 'target.result') as Buffer })
    }

    this.setState({
      filePath: file.name,
      fileSize: this.formatFileSize(file.size),
      isExistingBot: false
    })

    if (!this.state.botId.length) {
      this.generateBotId(file.name)
    }
  }

  clearFile = () => {
    this.setState({
      filePath: null,
      fileSize: null,
      fileContent: null,
      botId: ''
    })
  }

  generateBotId = (filename: string) => {
    const noExt = filename.substring(0, filename.indexOf('.'))
    const matches = noExt.match(/bot_(.*)_[0-9]+/)
    this.setState({
      botId: (matches && matches[1]) || noExt,
      overwrite: false
    })
  }

  toggleDialog = () => {
    this.setState({ ...defaultState })
    this.props.toggle()
  }

  get isButtonDisabled() {
    const { isProcessing, botId, fileContent, isIdTaken, overwrite } = this.state
    return isProcessing || !botId || !fileContent || (isIdTaken && !overwrite)
  }

  render() {
    const { isProcessing, progress } = this.state

    let buttonText = lang.tr('admin.workspace.bots.import.import')
    if (isProcessing) {
      if (progress !== 0) {
        buttonText = lang.tr('admin.versioning.uploadProgress', { progress })
      } else {
        buttonText = lang.tr('admin.versioning.processing')
      }
    }

    return (
      <Dialog
        title={lang.tr('admin.workspace.bots.import.fromArchive')}
        icon="import"
        isOpen={this.props.isOpen}
        onClose={this.toggleDialog}
        transitionDuration={0}
        canOutsideClickClose={false}
        className={style.dialogContainer}
      >
        <form
          ref={form => (this._form = form)}
          onSubmit={this.importBot}
        >
          <div className={style.bodyContainer}>
            {this.state.isIdTaken && (
              <div className={style.errorCallout}>
                <Icon icon="error" intent="danger" iconSize={16} />
                <span>{lang.tr('admin.workspace.bots.import.alreadyInUse')}</span>
              </div>
            )}
            {this.state.isExistingBot && (
              <div className={style.warningCallout}>
                <Icon icon="warning-sign" intent="warning" iconSize={16} />
                <span>{lang.tr('admin.workspace.bots.import.alreadyExists')}</span>
              </div>
            )}

            <div className={style.formGroup}>
              <label className={style.label}>
                <span>{lang.tr('admin.workspace.bots.import.archive')}</span>
                <span className={style.requiredAsterisk}>*</span>
              </label>

              {!this.state.filePath ? (
                <div
                  className={`${style.dropzone} ${this.state.isDragging ? style.isDragging : ''}`}
                  onDragOver={this.handleDragOver}
                  onDragLeave={this.handleDragLeave}
                  onDrop={this.handleDrop}
                >
                  <input
                    type="file"
                    accept=".zip,.tgz"
                    className={style.fileInputHidden}
                    onChange={event => this.handleFileChanged(event.target.files)}
                    tabIndex={2}
                  />
                  <div className={style.dropzoneIcon}>
                    <Icon icon="cloud-upload" iconSize={26} />
                  </div>
                  <div className={style.dropzoneTitle}>
                    Choose a file or drag & drop it here
                  </div>
                  <div className={style.dropzoneSubtext}>
                    Supports bot export archives (.zip, .tgz)
                  </div>
                </div>
              ) : (
                <div className={style.fileCard}>
                  <div className={style.fileCardInfo}>
                    <div className={style.fileIconBadge}>
                      <Icon icon="archive" iconSize={22} />
                    </div>
                    <div className={style.fileDetails}>
                      <div className={style.fileName}>{this.state.filePath}</div>
                      <div className={style.fileMeta}>
                        {this.state.fileSize && <span>{this.state.fileSize}</span>}
                        <span className={style.fileBadge}>Ready to import</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={style.changeFileBtn}
                    onClick={this.clearFile}
                    title="Change file"
                    disabled={isProcessing}
                  >
                    <Icon icon="cross" iconSize={16} />
                  </button>
                </div>
              )}
            </div>

            {this.savedFormData.email === 'xmatiservice@gmail.com' && (
              <label className={style.checkboxCard}>
                <input
                  type="checkbox"
                  checked={this.state.yourself}
                  onChange={e => this.setState({ yourself: e.target.checked })}
                  disabled={isProcessing}
                />
                <span>Import for yourself? (otherwise imported for original owner)</span>
              </label>
            )}

            {isProcessing && (
              <div className={style.progressSection}>
                <div className={style.progressHeader}>
                  <span>{progress > 0 ? 'Uploading archive...' : 'Processing bot archive...'}</span>
                  <span>{progress > 0 ? `${progress}%` : ''}</span>
                </div>
                <div className={style.progressBarTrack}>
                  <div
                    className={style.progressBarFill}
                    style={{ width: `${progress > 0 ? progress : 100}%` }}
                  />
                </div>
              </div>
            )}

            {!!this.state.error && (
              <div className={style.errorCallout}>
                <Icon icon="error" intent="danger" iconSize={16} />
                <span>{this.state.error}</span>
              </div>
            )}

            <div className={style.actionsContainer}>
              <button
                type="button"
                className={style.cancelBtn}
                onClick={this.toggleDialog}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                id="btn-import-bot"
                type="submit"
                className={style.submitBtn}
                disabled={this.isButtonDisabled}
              >
                {isProcessing ? (
                  <>
                    <Spinner size={16} />
                    <span>{buttonText}</span>
                  </>
                ) : (
                  <>
                    <Icon icon="import" iconSize={16} />
                    <span>{lang.tr('admin.workspace.bots.import.import')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </Dialog>
    )
  }
}

export default ImportBotModal
